import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Form, Spin } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

import {
  selectProvidersAllFacilities,
  selectFacilityById,
  selectUsers,
  selectCanEditFacilities
} from "../../store/selector";
import ACTIONS from "../../store/action";
import { FACILITY_UPDATE_ERROR_TYPES } from "../../store/saga/sagaActions";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import { RouterPrompt } from "../../router/RouterPrompt";
import {
  BreadcrumbSection,
  ButtonRow,
  CancelButton,
  SavingSpinner
} from "../../component/sharedComponents/settingsEntity/components";
import {
  CompactDetailPageContainer,
  CompactDetailHeaderSection,
  PageHeading
} from "../../component/sharedComponents/settingsEntity/detail";
import { PRIMARY_600 } from "../../constant/colors";
import { useStandardizedTracking } from "../../utils/pendo/useStandardizedTracking";
import { getChangedFields as getChangedFieldsForKeys } from "../../component/sharedComponents/settingsEntity/utils";
import { FacilityForm, FACILITY_EDITABLE_FIELDS } from "./FacilityForm";
import { FacilityBreadcrumb, FacilityMetaLine, FacilityHeaderButton } from "./FacilityDetail";
import { FACILITY_EVENTS, FACILITY_FEATURE_GROUP, FACILITY_PAGE_KEY } from "./analytics";

// Figma header actions (4036:28815/28818): Save keeps its 100×32 when the label swaps for the spinner.
const FacilitySaveButton = FacilityHeaderButton;

// Tertiary hover/pressed per TMCore 878:4797, same treatment as MVP-32058's User Management Cancel.
// rgba literal because PRIMARY_600 is a hex string and the repo has no hex-to-rgba helper.
const FacilityCancelButton = styled(CancelButton)`
  height: 32px;
  padding: 4px 8px;
  font-family: "Open Sans";
  line-height: 150%;
  color: ${PRIMARY_600} !important;
  border-bottom: 1.5px solid ${PRIMARY_600} !important;

  &:hover,
  &:focus,
  &:active {
    color: ${PRIMARY_600} !important;
    border-bottom: 1.5px solid ${PRIMARY_600} !important;
  }

  /* the base CancelButton's :focus wash sticks after a mouse click — reset it */
  &:focus {
    background: transparent !important;
  }

  &:hover {
    background: linear-gradient(180deg, rgba(21, 112, 239, 0) 0%, rgba(21, 112, 239, 0.2) 100%) !important;
  }

  /* pressed or keyboard focus, not the focus a mouse click leaves behind */
  &:active,
  &:focus-visible {
    background: linear-gradient(180deg, rgba(21, 112, 239, 0) 0%, rgba(21, 112, 239, 0.3) 100%) !important;
  }
`;

// Which of the editable fields differ from the loaded facility (for analytics + dirty tracking).
export const getChangedFields = (formValues, initialValues) =>
  getChangedFieldsForKeys(FACILITY_EDITABLE_FIELDS, formValues, initialValues);

export const toInitialValues = (facility) =>
  FACILITY_EDITABLE_FIELDS.reduce((acc, key) => ({ ...acc, [key]: facility[key] ?? undefined }), {});

export const FacilityFormPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { track } = useStandardizedTracking();
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  const facilities = useSelector(selectProvidersAllFacilities);
  const users = useSelector(selectUsers) || {};
  const canEdit = useSelector(selectCanEditFacilities);

  const facilitySelector = useMemo(() => selectFacilityById(id), [id]);
  const facility = useSelector(facilitySelector);
  const isLoaded = facilities.length > 0;

  const [initialValues, setInitialValues] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Live name-uniqueness check: { status: "idle" | "checking" | "unique" | "taken", providerId?, message? }
  const [nameCheck, setNameCheck] = useState({ status: "idle" });
  const [pendingNavigationPath, setPendingNavigationPath] = useState(null);
  const errorOccurredRef = useRef(false);

  const trackEvent = useCallback(
    (eventName, contentKey, additionalInfo = {}) =>
      track(eventName, {
        featureGroup: FACILITY_FEATURE_GROUP,
        pageKey: FACILITY_PAGE_KEY,
        contentKey,
        additionalInfo: { facility_id: Number(id), ...additionalInfo }
      }),
    [track, id]
  );

  useEffect(() => {
    if (!isLoaded) dispatch(ACTIONS.providersAction("facility"));
  }, [dispatch, isLoaded]);

  // Not found → list. Not allowed to edit → read-only detail (closes the direct-URL backdoor).
  useEffect(() => {
    if (isLoaded && !facility) history.push("/facilities-settings");
    else if (facility && !canEdit) history.push(`/facilities-settings/${facility.id}`);
  }, [isLoaded, facility, canEdit, history]);

  useEffect(() => {
    if (facility && !initialValues) setInitialValues(toInitialValues(facility));
  }, [facility, initialValues]);

  useEffect(() => {
    if (!initialValues || !formValues) return;
    setHasUnsavedChanges(getChangedFields(formValues, initialValues).length > 0);
  }, [formValues, initialValues]);

  // Post-save navigation must wait for the re-render that disarms RouterPrompt's history.block
  // guard (React 19 batches setHasUnsavedChanges(false)) — same pattern as PhysicianFormPage.
  useEffect(() => {
    if (pendingNavigationPath && !hasUnsavedChanges) history.push(pendingNavigationPath);
  }, [pendingNavigationPath, hasUnsavedChanges, history]);

  const handleFieldsChange = useCallback((changedFields, allFields) => {
    setHasValidationErrors(allFields.some((field) => field.errors && field.errors.length > 0));
  }, []);

  const NAME_CHECK_DEBOUNCE_MS = 500;

  // Debounced pre-flight uniqueness check on every keystroke (same UX as the MRN field on Create New
  // Patient): spinner while checking, green check when unique, red icon + message when taken.
  const debouncedNameCheck = useMemo(
    () =>
      debounce((trimmed, facilityId) => {
        dispatch(
          ACTIONS.validateFacilityNameAction(trimmed, facilityId, (result) => {
            setNameCheck(
              result.isUnique
                ? { status: result.unavailable ? "idle" : "unique" }
                : { status: "taken", providerId: result.providerId, message: result.message }
            );
          })
        );
      }, NAME_CHECK_DEBOUNCE_MS),
    [dispatch]
  );

  useEffect(() => () => debouncedNameCheck.cancel(), [debouncedNameCheck]);

  const handleNameChange = useCallback(
    (value) => {
      const trimmed = (value || "").trim();
      // Empty or unchanged name → nothing to check
      if (!facility || !trimmed || trimmed.toLowerCase() === (facility.name || "").trim().toLowerCase()) {
        debouncedNameCheck.cancel();
        setNameCheck({ status: "idle" });
        return;
      }
      setNameCheck({ status: "checking" });
      debouncedNameCheck(trimmed, facility.id);
    },
    [facility, debouncedNameCheck]
  );

  const handleCancel = () => {
    trackEvent(FACILITY_EVENTS.EDIT_CANCELLED, "cancel_button");
    history.push(`/facilities-settings/${id}`);
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        setIsSaving(true);
        errorOccurredRef.current = false;
        const payload = FACILITY_EDITABLE_FIELDS.reduce((acc, key) => ({ ...acc, [key]: values[key] ?? null }), {});
        const fieldsChanged = getChangedFields(values, initialValues);

        dispatch(
          ACTIONS.updateFacilityAction(
            facility.id,
            payload,
            () => {
              if (errorOccurredRef.current) return;
              setIsSaving(false);
              setHasUnsavedChanges(false);
              trackEvent(FACILITY_EVENTS.EDIT_SAVED, "save_button", { fields_changed: fieldsChanged });
              SuccessMessage(t("facility_management.form.success_updated"));
              setPendingNavigationPath("/facilities-settings");
            },
            (error) => {
              errorOccurredRef.current = true;
              setIsSaving(false);
              if (error?.type === FACILITY_UPDATE_ERROR_TYPES.DUPLICATE_NAME) {
                setNameCheck({ status: "taken", providerId: error.providerId, message: error.message });
              }
            }
          )
        );
      })
      .catch(() => {
        // validation errors are rendered inline
      });
  };

  if (!facility || !initialValues) {
    return (
      <CompactDetailPageContainer>
        <Spin />
      </CompactDetailPageContainer>
    );
  }

  return (
    <>
      <RouterPrompt
        shouldTriggerPopup={hasUnsavedChanges}
        popupDescription={t("facility_management.modal.unsaved_message")}
        popupTitle={t("facility_management.modal.unsaved_title")}
        okText={t("yes_sure")}
        usePrimaryButton
        onPromptShown={() => trackEvent(FACILITY_EVENTS.UNSAVED_CHANGES_SHOWN, "unsaved_changes", { context: "edit" })}
        onDiscard={() => trackEvent(FACILITY_EVENTS.UNSAVED_CHANGES_DISCARDED, "unsaved_changes", { context: "edit" })}
      />
      <CompactDetailPageContainer id="Facility_Edit_Page">
        <CompactDetailHeaderSection>
          <BreadcrumbSection>
            <FacilityBreadcrumb current={facility.name} history={history} t={t} />
            <ButtonRow>
              <FacilityCancelButton id="settings-facility-edit-cancel-btn" type="text" onClick={handleCancel}>
                {t("facility_management.form_page.button_cancel")}
              </FacilityCancelButton>
              <FacilitySaveButton
                id="settings-facility-edit-save-btn"
                type="primary"
                onClick={handleSubmit}
                disabled={
                  isSaving ||
                  !hasUnsavedChanges ||
                  hasValidationErrors ||
                  nameCheck.status === "taken" ||
                  nameCheck.status === "checking"
                }
                aria-busy={isSaving}
                aria-label={isSaving ? t("facility_management.form_page.button_saving") : undefined}
                icon={isSaving ? <SavingSpinner /> : undefined}
              >
                {isSaving ? null : t("facility_management.form_page.button_save")}
              </FacilitySaveButton>
            </ButtonRow>
          </BreadcrumbSection>
          <PageHeading>{t("facility_management.detail.page_title")}</PageHeading>
          <FacilityMetaLine facility={facility} users={users} t={t} />
        </CompactDetailHeaderSection>

        <FacilityForm
          form={form}
          initialValues={initialValues}
          matchingStrings={facility.providerMatchingAliases}
          nameCheck={nameCheck}
          onFieldsChange={handleFieldsChange}
          onNameChange={handleNameChange}
        />
      </CompactDetailPageContainer>
    </>
  );
};

export default FacilityFormPage;
